import { FC, useEffect, useCallback, useRef } from 'react';
import { Typography, Skeleton, Box } from '@mui/material';

import { Employer, Rooftop } from '@libs/models';
import { useEmployerIndexQuery, employerApi } from '@carcareers/store';
import { ListInput, EmployerSelect } from '@carcareers/components';
import { useForm } from '@carcareers/contexts';

interface RooftopPermissionsInputProps {
  rooftop?: Rooftop;
}

interface SearchParams {
  q: string;
  page?: number;
  limit?: number;
}

export const RooftopPermissionsInput: FC<RooftopPermissionsInputProps> = ({ rooftop }) => {
  const { getData, setData } = useForm<Rooftop>();
  const [searchEmployer] = employerApi.endpoints.employerSearch.useLazyQuery();
  const { isLoading } = useEmployerIndexQuery({ page: 1, limit: 10 });
  const initializedRef = useRef(false);

  const wrappedSearch = useCallback(async (params: SearchParams) => {
    try {
      const result = await searchEmployer(params);
      return result.data;
    } catch (error) {
      console.error('Search error:', error);
      return { items: [] };
    }
  }, [searchEmployer]);

  useEffect(() => {
    if (rooftop?.owner && !getData('owner') && !initializedRef.current) {
      setData('owner', rooftop.owner);
      initializedRef.current = true;
    }
  }, [rooftop, getData, setData]);

  const getDefaultOwner = useCallback(() => {
    const ownerData = getData<Rooftop['owner']>('owner');
    if (ownerData && ownerData.id) {
      return { 
        label: `${ownerData.id} - ${ownerData.user?.email}`, 
        value: ownerData 
      };
    } 
    if (rooftop?.owner && rooftop.owner.id) {
      return { 
        label: `${rooftop.owner.id} - ${rooftop.owner.user?.email}`, 
        value: rooftop.owner 
      };
    }
    return undefined;
  }, [getData, rooftop]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">Permissions</Typography>
      {isLoading ? (
        <Skeleton variant="rounded" height={56} />
      ) : (
        <EmployerSelect 
          name="owner" 
          defaultValue={getDefaultOwner()} 
        />
      )}
      {isLoading ? (
        <Skeleton variant="rounded" height={56} />
      ) : (
        <ListInput<Employer>
          name="authorizedEmployers"
          label="Add Authorized Employers"
          search={wrappedSearch}
        />
      )}
    </Box>
  );
};

export default RooftopPermissionsInput;
